import "../css/Dashboard.css";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppSidebar from "../components/AppSidebar";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ProtectedRoute from "../components/ProtectedRoute";
import ThemeToggle from "../components/ThemeToggle";
import { AuthContext } from "../context/AuthContext";

const titles: Record<string, { titleKey: string; subKey?: string }> = {
  "/dashboard": { titleKey: "common.dashboard", subKey: "layout.dashboardSubtitle" },
  "/profile": { titleKey: "common.myProfile" },
  "/admin/users": { titleKey: "common.adminPanel", subKey: "layout.adminSubtitle" },
  "/projects": { titleKey: "common.projects" },
  "/publications": { titleKey: "common.publications" },
  "/announcements": { titleKey: "common.announcements" },
  "/messages": { titleKey: "common.messages" },
  "/matching": { titleKey: "common.matching" },
  "/auto-matching": { titleKey: "common.autoMatching" },
  "/connections": { titleKey: "common.connections" },
  "/connections/requests": { titleKey: "common.requests" }
};

export default function ProtectedLayout() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { user } = useContext(AuthContext);
  const canAccessAdminPanel = user?.role === "ADMIN" || user?.role === "SUPERVISOR";
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    const onClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) setMobileMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onClickOutside);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    const hardReset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });

      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
        mainRef.current.scrollLeft = 0;
      }

      const content = document.querySelector(".content");
      if (content instanceof HTMLElement) {
        content.scrollTop = 0;
        content.scrollLeft = 0;
      }

      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.getAttribute("contenteditable") === "true")
      ) {
        active.blur();
      }
    };

    hardReset();
    requestAnimationFrame(hardReset);
    setTimeout(hardReset, 50);
    setTimeout(hardReset, 250);
  }, [location.pathname, location.search, location.hash]);

  const go = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const meta = titles[location.pathname] ?? { titleKey: "layout.defaultTitle" };

  return (
    <ProtectedRoute>
      <div className="dashboard-layout">
        <AppSidebar />

        <main className="dashboard-main" ref={mainRef}>
          <header className="topbar">
            <div className="topbar-left">
              <h2>{t(meta.titleKey)}</h2>
              {meta.subKey ? <span className="topbar-sub">{t(meta.subKey)}</span> : null}
            </div>

            <div className="topbar-actions">
              <ThemeToggle />
              <LanguageSwitcher />

              <div className="mobileMenu" ref={menuRef}>
                <button
                  className="mobileMenuBtn"
                  type="button"
                  onClick={() => setMobileMenuOpen((current) => !current)}
                  aria-label={t("layout.openMenu")}
                >
                  {t("common.menu")}
                </button>

                {mobileMenuOpen && (
                  <div className="mobileMenuPanel" role="menu">
                    <button type="button" onClick={() => go("/dashboard")}>
                      {t("common.dashboard")}
                    </button>
                    <button type="button" onClick={() => go("/profile")}>
                      {t("common.myProfile")}
                    </button>

                    {canAccessAdminPanel && (
                      <>
                        <div className="mobileMenuSep" />

                        <button type="button" onClick={() => go("/admin/users")}>
                          {t("common.adminPanel")}
                        </button>
                      </>
                    )}

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/projects")}>
                      {t("common.projects")}
                    </button>
                    <button type="button" onClick={() => go("/publications")}>
                      {t("common.publications")}
                    </button>
                    <button type="button" onClick={() => go("/announcements")}>
                      {t("common.announcements")}
                    </button>
                    <button type="button" onClick={() => go("/messages")}>
                      {t("common.messages")}
                    </button>

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/matching")}>
                      {t("common.matching")}
                    </button>
                    <button type="button" onClick={() => go("/auto-matching")}>
                      {t("common.autoMatching")}
                    </button>

                    <div className="mobileMenuSep" />

                    <button type="button" onClick={() => go("/connections")}>
                      {t("common.connections")}
                    </button>
                    <button type="button" onClick={() => go("/connections/requests")}>
                      {t("common.requests")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <section className="content">
            <Outlet />
          </section>
        </main>
      </div>
    </ProtectedRoute>
  );
}
